/**
 * Button.js
 *
 * Created on 23-sep-2013
 *
 * @author Reinier Feijen for Ranj
 *
 * Button wrapper that works with createjs and exported RanjAutoButton skins from flash.
 * Contains functionality for showing attract state (setAttract). For setAttract to work, skin should have instance 'attract'.
 * Contains functionality for text buttons (setLabel). For setLabel to work, skin should have properly named textfields.
 * Contains functionality for selectable buttons/toggles (setSelected), with knwon frame labels.
 *  *
 * Known issues:
 * - In this script, skin.hit is now set to alpha .01. This should not happen here, but be managed in assets.
 *   It could be by design that the skin.hit is not transparent!
 **/

import { isUndefined } from '../../util/TypeUtil';
import MouseUtil from '../../util/MouseUtil';
import TextWrap from '../text/TextWrap';

class Button {
	constructor(skin, wordWrap = false) {
		this.skin = skin;
		this.skin.mouseChildren = false;
		//
		this._handleClickBound = this._handleClick.bind(this);
		this._handleDoubleClickBound = this._handleDoubleClick.bind(this);
		this._handleMouseDownBound = this._handleMouseDown.bind(this);
		this._handleMouseMoveBound = this._handleMouseMove.bind(this);
		this._handleMouseUpBound = this._handleMouseUp.bind(this);
		this._handleMouseOverBound = this._handleMouseOver.bind(this);
		this._handleMouseOutBound = this._handleMouseOut.bind(this);
		this._handleRollOverBound = this._handleRollOver.bind(this);
		this._handleRollOutBound = this._handleRollOut.bind(this);
		this._handleTickBound = this._handleTick.bind(this);

		this.target = this.skin;
		//Workaround to populate this.skin.children right away 
		//  Container.children prop of assets exported from a fla is empty when asset is just constructed
		//  and only gets filled in the first update after construction!
		//  Calling gotoAndStop forces the children prop to be filled.
		if (this.skin.hasOwnProperty('gotoAndStop')) {
			this.skin.gotoAndStop(0);
		}
		//Setting autoReset=false avoids the button to start playing when (re)added to its parent later on a timeline
		this.skin.autoReset = false;
		if (!isUndefined(this.skin.hit)) {
			this.target = this.skin.hit;
			if (this.target.alpha < .01)
				this.target.alpha = .01;
			this.skin.mouseChildren = true;
			//set mouseEnabled on other children of skin to false
			//  to avoid mouse events being blocked.
			for (var i = 0; i < this.skin.numChildren; i++) {
				var child = this.skin.getChildAt(i);
				if (child !== this.target)
					child.mouseEnabled = false;
			}
		}

		this._ignoreInteractionFunction = function (e) {
			return false;
		};

		this.target.addEventListener("click", this._handleClickBound);
		this.target.addEventListener("dblclick", this._handleDoubleClickBound);
		this.target.addEventListener("mousedown", this._handleMouseDownBound);
		this.target.addEventListener("pressup", this._handleMouseUpBound);
		this.target.addEventListener("pressmove", this._handleMouseMoveBound);
		this.target.addEventListener("mouseover", this._handleMouseOverBound);
		this.target.addEventListener("mouseout", this._handleMouseOutBound);
		this.target.addEventListener("rollover", this._handleRollOverBound);
		this.target.addEventListener("rollout", this._handleRollOutBound);

		//
		this._label = null;
		this._selected = false;
		this._attract = false;
		this._mouseIsDown = false;
		this._mouseIsOver = false;
		this._mouseEvent = null;//the mouse event used for up/move listenting. EaselJS 0.6 and lower!
		this.setEnabled(true);

		this._cacheEnabled = false;
		this._cacheIsDirty = false;
		this._cacheBounds = null;

		var cacheEnabled = Button.cacheEnabledDefault;
		this.setCacheEnabled(cacheEnabled);

		var setHitAreaToBounds = Button.setHitAreaToBoundsDefault;
		var hasHitArea = this.skin && this.skin.hitArea;
		if (setHitAreaToBounds && !hasHitArea) {
			this.setHitAreaToBounds();
		}
		
		this._wordWrap = wordWrap;

		Button.dispatcher.dispatchEvent({ type: Button.CREATED, button: this });
	};

	destroy() {
		if (this._showingPointer)
			this._showPointer(false);
		if (this.skin) {
			this.target.removeEventListener("click", this._handleClickBound);
			this.target.removeEventListener("dblclick", this._handleDoubleClickBound);
			this.target.removeEventListener("mousedown", this._handleMouseDownBound);
			this.target.removeEventListener("pressup", this._handleMouseUpBound);
			this.target.removeEventListener("pressmove", this._handleMouseMoveBound);
			this.target.removeEventListener("mouseout", this._handleMouseOutBound);
			this.target.removeEventListener("mouseover", this._handleMouseOverBound);
			this.target.removeEventListener("rollover", this._handleRollOverBound);
			this.target.removeEventListener("rollout", this._handleRollOutBound);
			this.target.removeEventListener("added", this._handleAddedToStageBound);
			this.setCacheEnabled(false); // removes tick event listener when added
			this.skin.mouseChildren = false;

			this.skin = null;
		}
		//this._resetMouseEvent();
		this.removeAllEventListeners();

		if (this._data) {
			this._data = null;
		}

		Button.dispatcher.dispatchEvent({ type: Button.DESTROYED, button: this });
	};

	/**
	 * Function that should return true if interaction should be ignored. Function should take a mouse event as an argument
	 * @param {type} fn the function that determines whether interaction should be ignored.
	 */
	setIgnoreInteractionFunction(fn) {
		this._ignoreInteractionFunction = fn;
	};

	//  ****************** KeyboardFocusManager related **********************

	getFocus() {
		return this._hasFocus;
	};
	setFocus(bool) {
		this._hasFocus = bool;
		this._setState(!this._enabled ? Button.DISABLED : (bool ? Button.OVER : Button.UP));
	};
	getFocusTarget() {
		return this.skin;
	};
	getFocusBounds(r) {
		//TODO/WISH: add method for not using focus rect for this button?
		//WISH: Better way of determining bounds
		var b = this.target.nominalBounds;
		if (!b)
			b = this.target.getBounds();
		if (!b)
			return null;
		var p0 = this.target.localToGlobal(b.x, b.y);
		var p1 = this.target.localToGlobal(b.x + b.width, b.y + b.height);
		if (!r)
			r = new Rectangle();
		r.x = Math.min(p0.x, p1.x);
		r.width = Math.max(p0.x, p1.x) - r.x;
		r.y = Math.min(p0.y, p1.y);
		r.height = Math.max(p0.y, p1.y) - r.y;
		return r;
	};
	/**
	 * Called by KeyboardFocusManager when user presses enter when this button is focussed.
	 */
	onClick() {
		//create fake mouse event
		var e = new MouseEvent("click", false, false, this.skin.x, this.skin.y, false);
		e.target = e.currentTarget = this.target;
		this._handleClick(e);
	};


	//  ****************** GETTERS / SETTERS **********************

	// get/set arbitrary data on the button
	// can be used for anything
	getData() {
		return this._data;
	};
	setData(data) {
		this._data = data;
	};

	getSelected() {
		return this._selected;
	};
	setSelected(bool) {
		this._selected = bool;
		this._setState(this._state);
	};
	getVisible() {
		return this.skin.visible;
	};
	setVisible(bool) {
		if (this.skin.visible !== bool) {
			this.skin.visible = bool;
			this._changed();
		}
	};
	getEnabled() {
		return this._enabled;
	};
	setEnabled(bool) {
		this._enabled = bool;
		if (!bool) {
			this._setState(Button.DISABLED);
			this._showPointer(false);
			//this._resetMouseEvent();
		}
		else {
			this._setState(Button.UP);
		}
	};
	setAttract(bool) {
		this._attract = bool;
		if (this.skin && this.skin.attract) {
			this.skin.attract.visible = bool;

			this._changed();
		}
	};
	getAttract() {
		return this._attract;
	};
	setLabel(string) {
		this._label = string;
		if (this.skin) {
			for (var i = 0; i < Button.TEXTFIELDNAMES.length; i++) {
				var name = Button.TEXTFIELDNAMES[i];
				if (this.skin[name]) {
					this.skin[name].text = this._label ? this._label : "";
					if(this._wordWrap) {
						this._applyWordWrap(this.skin[name]);
					}
				}
			}

			this._changed();
		}
	};
	getLabel() {
		return this._label;
	};

	/**
	 * Set label and center textfields vertically
	 * @param {String} string
	 */
	setCenteredLabel(string) {
		if (this._label === string)
			return;
		this._label = string;
		if (this.skin) {
			for (var i = 0; i < Button.TEXTFIELDNAMES.length; i++) {
				var name = Button.TEXTFIELDNAMES[i];
				var tf = this.skin[name];
				if (tf) {
					if (!this._labelCentered && !tf.y0 && !tf.h0) {
						tf.y0 = tf.y;
						tf.h0 = tf.getMeasuredHeight() * tf.scaleY;
					}
					var y0 = tf.y0;
					var h0 = tf.h0;
					tf.text = this._label ? this._label : "";
					if(this._wordWrap) {
						this._applyWordWrap(tf);
					}
					var h1 = tf.getMeasuredHeight() * tf.scaleY;
					var y1 = y0 + .5 * (h0 - h1);
					tf.y = y1;
				}
			}
			this._labelCentered = true;

			this._changed();
		}
	};

	/**
	 * Center the label vectically in the textfield
	 * @param {Number} textFieldHeight
	 */
	centerLabelVertically(textFieldHeight) {
		if (this.skin) {
			for (var i = 0; i < Button.TEXTFIELDNAMES.length; i++) {
				var name = Button.TEXTFIELDNAMES[i];
				var textField = this.skin[name];
				if (textField) {
					if (isUndefined(textField.y0)) {
						textField.y0 = textField.y;
					}
					var textHeight = textField.getMeasuredHeight();
					textField.y = textField.y0 + textFieldHeight / 2 - textHeight / 2;
				}
			}

			this._changed();
		}
	};

	//  ****************** MOUSE EVENT LISTENERS **********************

	_handleMouseOver(e) {
		return;
		/*
//console.log("Button: mouse OVER (enabled = " + this._enabled + ")");
if (!this._enabled)
	return;
this._mouseIsOver = true;
this._showPointer(true);
if (!this._mouseIsDown)
{
	this._setState(Button.OVER);
	this.dispatchEvent(e);
}
		*/
	};
	_handleMouseOut(e) {
		return;
		/*
//console.log("Button: mouse OUT (enabled = " + this._enabled + ")");
if (!this._enabled)
	return;
this._mouseIsOver = false;
this._showPointer(false);
if (!this._mouseIsDown)
	this._setState(Button.UP);
this.dispatchEvent(e);
		*/
	};
	_handleRollOver(e) {
		//console.log("Button: mouse OVER (enabled = " + this._enabled + ")");
		if (!this._enabled)
			return;
		if (this._ignoreInteractionFunction(e))
			return;
		this._mouseIsOver = true;
		this._showPointer(true);
		if (!this._mouseIsDown) {
			this._setState(Button.OVER);
			this.dispatchEvent(e);
			;
		}
	};
	_handleRollOut(e) {
		//console.log("Button: mouse OUT (enabled = " + this._enabled + ")");
		if (!this._enabled)
			return;
		if (this._ignoreInteractionFunction(e))
			return;
		this._mouseIsOver = false;
		this._showPointer(false);
		if (!this._mouseIsDown)
			this._setState(Button.UP);
		this.dispatchEvent(e);
	};
	_handleMouseDown(e) {
		//console.log("Button: mouse DOWN (enabled = " + this._enabled + ")");
		//console.log("Mouse Down Enabled: " + this._enabled);
		//console.log("down");
		if (!this._enabled)
			return;
		if (this._ignoreInteractionFunction(e))
			return;

		this._mouseIsDown = true;
		//
		this._mouseEvent = e;
		this._setState(Button.DOWN);
		this.dispatchEvent(e);
	};
	_handleMouseUp(e) {
		//console.log("Mouse Up Enabled: " + this._enabled);
		//console.log("up");
		this._mouseIsDown = false;

		if (!this._enabled)
			return;
		if (this._ignoreInteractionFunction(e))
			return;

		this._setState(Button.UP);
		this.dispatchEvent(e);
	};
	_handleMouseMove(e) {
		if (!this._enabled)
			return;
		if (this._ignoreInteractionFunction(e))
			return;
		this.dispatchEvent(e);
	};
	_handleClick(e) {
		if (!this._enabled)
			return;
		if (this._ignoreInteractionFunction(e))
			return;
		//console.log("click");
		this._setState(this._mouseIsOver ? Button.OVER : Button.UP);
		//reset attract state
		this.setAttract(false);
		this.dispatchEvent(e);
	};
	_handleDoubleClick(e) {
		//console.log("Button: mouse DOUBLE CLICK (enabled = " + this._enabled + ")");
		if (!this._enabled)
			return;
		if (this._ignoreInteractionFunction(e))
			return;
		this._setState(this._mouseIsOver ? Button.OVER : Button.UP);
		this.dispatchEvent(e);
	};


	//  ****************** STATE **********************


	_setState(state) {
		var stateOld = this._state;
		this._state = state;
		var unselectedState = state;
		if (this.skin.gotoAndStop) {
			if (this._selected) {
				state = "selected" + state.substr(0, 1).toUpperCase() + state.substr(1);
				stateOld = "selected" + stateOld.substr(0, 1).toUpperCase() + stateOld.substr(1);
			}
			//Check existence of transition between states
			//NB: transition animations will not work with cache mechanism!
			var transitionLabel = this._getStateTransitionLabel(stateOld, state);
			if (transitionLabel) {
				this.skin.gotoAndPlay(transitionLabel);
			}
			else {
				this.skin.gotoAndStop(state);
			}
		}
		//hide all labels
		for (var i = 0; i < Button.TEXTFIELDNAMES.length; i++) {
			var name = Button.TEXTFIELDNAMES[i];
			if (this.skin[name])
				this.skin[name].visible = false;
		}
		//show active label
		name = state + "LabelTextField";//TODO: use TEXTFIELDNAMES ?
		if (this.skin[name]) {
			this.skin[name].visible = true;
		} else {
			// Fallback for selectable buttons that have no labels in their selected states: use the labels of the non-selected states.
			var unselectedName = unselectedState + "LabelTextField";
			if (this.skin[unselectedName]) {
				this.skin[unselectedName].visible = true;
			}
		}

		this._changed();
	};

	getState() {
		return this._state;

	};

	/**
	 * Check existence of transition between states and return label for transition
	 * Transitions should be labelled "up_over" etc.
	 * @param {string} stateFrom
	 * @param {string} stateTo
	 * @returns {string} Label for transition or null
	 */
	_getStateTransitionLabel(stateFrom, stateTo) {
		var transitionLabel = stateFrom + "_" + stateTo;
		var labels = this.skin.labels;//array of objs with props: label (string), position (frameindex)
		for (var i = 0; i < labels.length; i++) {
			if (labels[i].label == transitionLabel) {
				return transitionLabel;
			}
		}
		return null;
	}


	_showPointer(bool) {
		MouseUtil.showPointer(bool);
		this._showingPointer = bool;
	};

	_resetMouseEvent() {
		//Only needed for easeljs 0.6 and lower
		if (this._mouseEvent) {
			this._mouseEvent.removeEventListener("pressup", this._handleMouseUpBound);
			this._mouseEvent.removeEventListener("pressmove", this._handleMouseMoveBound);
			this._mouseEvent = null;
		}
	};

	setHitArea(shape) {
		this.skin.hitArea = shape;
	};

	setHitAreaToBounds() {
		if (this.skin) {
			var bounds = this.skin.nominalBounds || this.skin.getBounds();
			var shape = new createjs.Shape();
			shape.graphics.beginFill("#f00").rect(bounds.x, bounds.y, bounds.width, bounds.height).endFill();
			//console.log("hitArea " + this.skin.name + ": " + bounds.x + " " + bounds.y + " " + bounds.width + " " + bounds.height);
			this.setHitArea(shape);
		}
	};

	isCacheEnabled() {
		return this._cacheEnabled;
	};

	setCacheEnabled(cacheEnabled) {
		if (this._cacheEnabled !== cacheEnabled) {
			this._cacheEnabled = cacheEnabled;
			if (this._cacheEnabled) {
				this.skin.addEventListener("tick", this._handleTickBound);
			} else {
				this.skin.removeEventListener("tick", this._handleTickBound);
			}
			this._changed();
		}
	};

	getCacheBounds() {
		return this._cacheBounds;
	};

	setCacheBounds(cacheBounds) {
		this._cacheBounds = cacheBounds;
		this._changed();
	};

	updateCache() {
		this._changed();
	};

	_updateCache() {
		if (this.skin) {
			if (this._cacheEnabled) {
				this._cacheIsDirty = true;
			} else {
				if (this.skin.cacheCanvas) {
					this.skin.uncache();
				}
			}
		}
	};

	changed() {
		this._changed();
	};

	_changed() {
		this._updateCache();
		this.dispatchEvent(Button.CHANGED);
		Button.dispatcher.dispatchEvent({ type: Button.CHANGED, button: this });
	};

	_handleTick() {
		if (this.skin && this._cacheEnabled && this._cacheIsDirty) {
			if (this.skin.cacheCanvas) {
				//console.log("Button updateCache " + this.skin.name);
				this.skin.updateCache();
			} else {
				//console.log("Button cache " + this.skin.name);
				var bounds = this._cacheBounds || this.skin.nominalBounds || this.skin.getBounds();
				this.skin.cache(bounds.x, bounds.y, bounds.width, bounds.height);
			}
			this._cacheIsDirty = false;
		}
	};

	set wordWrap(value) {
		this._wordWrap = value;
	}

	get wordWrap(){
		return this._wordWrap;
	}

	_applyWordWrap(textField) {
		TextWrap.wrapTextField(textField);
	}
}

Button.UP = "up";
Button.OVER = "over";
Button.DOWN = "down";
Button.DISABLED = "disabled";
Button.TEXTFIELDNAMES = [
	"upLabelTextField",
	"overLabelTextField",
	"downLabelTextField",
	"disabledLabelTextField",
	"selectedUpLabelTextField",
	"selectedOverLabelTextField",
	"selectedDownLabelTextField",
	"selectedDisabledLabelTextField"
];

Button.CREATED = 'ranj.Button.CREATED';
Button.DESTROYED = 'ranj.Button.DESTROYED';
Button.CHANGED = 'ranj.Button.CHANGED';

Button.cacheEnabledDefault = false;
Button.setHitAreaToBoundsDefault = false;
Button.dispatcher = new createjs.EventDispatcher();

//TODO: this will probably not work!!
createjs.EventDispatcher.initialize(Button.prototype);

export default Button;